import { useCallback, useState } from 'react';

import {
	EditSquareFilledIcon,
	EditSquareIcon,
	GovBuildingFilledIcon,
	GovBuildingIcon,
	HomeFilledIcon,
	HomeIcon,
	SpeakerFilledIcon,
	SpeakerIcon,
} from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import {
	ContentSection,
	MainContent,
	SideNavigation,
	SideNavigationItem,
	SideNavigationLayout,
} from '@change/ds-layout/next';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useStarterDashboardV2BreakpointState } from 'src/app/pages/starterDashboardV2/pageContext';
import {
	DM_TAB_CLICK,
	HOME_TAB_CLICK,
	SHARE_TAB_CLICK,
	SUPPORTERS_TAB_CLICK,
} from 'src/app/pages/starterDashboardV2/shared/constants';
import { ButtonVariantProvider } from 'src/app/pages/starterDashboardV2/shared/contexts';

import { DesktopTabs } from './components/DashboardTabs/components/DesktopTabs/index_new';
import { DecisionMakersList } from './components/DecisionMakersList';
import { RecentSupporters } from './components/EngageSupporters/components/RecentSupporters';
import { GrowPetition } from './components/GrowPetition';
import { NotificationsCenter } from './components/NotificationsCenter';
import { PetitionInsights } from './components/PetitionInsights';
import { PetitionOverview } from './components/PetitionOverview';
import { ReadyToShare } from './components/ReadyToShare';
import { Recommendations } from './components/Recommendations';
import { StarterDashboardComments } from './components/StarterDashboardComments';
import { StepsToVictory } from './components/StepsToVictory';
import { UpdateSupporters } from './components/UpdateSupporters';

const TAB_CLICK_EVENTS: Record<string, string> = {
	home: HOME_TAB_CLICK,
	share: SHARE_TAB_CLICK,
	dms: DM_TAB_CLICK,
	supporters: SUPPORTERS_TAB_CLICK,
};

const DesktopNavigation = () => {
	const { slug } = useKnownAvailableParams<{ slug: string }>();
	const track = useTracking();
	const switchTab = useCallback(
		(currentTab: string, previousTab: string) => {
			const event = TAB_CLICK_EVENTS[currentTab];
			void track(event, {
				context: previousTab,
			});
		},
		[track],
	);

	const [commentsVisibility, setCommentsVisibility] = useState(true);

	return (
		<SideNavigationLayout className="hidden md:block" onTabChange={switchTab} data-testid="page-body-reskin">
			<SideNavigation>
				<SideNavigationItem selectedIcon={<HomeFilledIcon />} icon={<HomeIcon />} id="home" label="Home" />
				<SideNavigationItem
					selectedIcon={<SpeakerFilledIcon />}
					icon={<SpeakerIcon />}
					id="share"
					label="Share your petition"
				/>
				<SideNavigationItem
					selectedIcon={<GovBuildingFilledIcon />}
					icon={<GovBuildingIcon />}
					id="dms"
					label="Connect to decision makers"
				/>
				<SideNavigationItem
					selectedIcon={<EditSquareFilledIcon />}
					icon={<EditSquareIcon />}
					id="supporters"
					label="Message your supporters"
				/>
			</SideNavigation>
			<MainContent>
				<ContentSection id="home" className="flex flex-col gap-6">
					<NotificationsCenter />
					<PetitionOverview />
					<Recommendations slug={slug} viewContext="home" />
					{/* eslint-disable-next-line @typescript-eslint/naming-convention */}
					<div className={cn('mb-4 grid grid-cols-[1fr] gap-x-4', { 'md:grid-cols-[1fr_1fr]': commentsVisibility })}>
						<RecentSupporters />
						{commentsVisibility && <StarterDashboardComments setCommentsVisibility={setCommentsVisibility} />}
					</div>
					<PetitionInsights />
				</ContentSection>

				<ContentSection id="share" className="flex flex-col gap-8">
					<ReadyToShare />
					<Recommendations slug={slug} viewContext="share" />
					<GrowPetition />
					<StepsToVictory />
				</ContentSection>

				<ContentSection id="dms" className="flex flex-col gap-8">
					<DecisionMakersList />
				</ContentSection>

				<ContentSection id="supporters" className="flex flex-col gap-8">
					<UpdateSupporters />
				</ContentSection>
			</MainContent>
		</SideNavigationLayout>
	);
};

const MobileNavigation = () => {
	return (
		<main className="col-span-4 md:hidden" data-testid="page-body-reskin">
			{/* TODO: Remove this provider once the tabs component has been migrated to sunrise */}
			{/* Naming is deceiving, even though the component's name is **Desktop** Tabs, we need to use it to render tabs in mobile */}
			<DesktopTabs />
		</main>
	);
};

export function PageBodyReskin() {
	const { isMobile } = useStarterDashboardV2BreakpointState();

	return (
		<ButtonVariantProvider data-qa="button-variant-context">
			{isMobile ? (
				<MobileNavigation data-test-id="mobile-navigation" />
			) : (
				<DesktopNavigation data-testid="desktop-navigation" />
			)}
		</ButtonVariantProvider>
	);
}
